import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag, MessageSquareReply, Pencil, Star, Trash2 } from "lucide-react";

import { StarRating } from "@/components/StarRating";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import type { ServiceType } from "@/lib/services";
import {
  REVIEW_SORTS,
  REVIEW_SORT_LABEL,
  deleteReview,
  hasVerifiedEnquiry,
  listListingReviews,
  reportReview,
  replyToReview,
  sortReviews,
  submitReview,
  summarize,
  updateOwnReview,
  type ReviewSort,
} from "@/lib/reviews";

interface Props {
  listingId: string;
  listingKind: string;
  serviceType: ServiceType;
  listingTitle: string | null;
  partnerId: string | null;
}

export function ReviewsSection({
  listingId,
  listingKind,
  serviceType,
  listingTitle,
  partnerId,
}: Props) {
  const { user, profile, hasRole } = useAuth();
  const qc = useQueryClient();
  const [sort, setSort] = useState<ReviewSort>("latest");
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [editing, setEditing] = useState(false);
  const [replyFor, setReplyFor] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const key = ["reviews", listingId];
  const { data: reviews = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => listListingReviews(listingId),
  });

  const { data: canReview = false } = useQuery({
    queryKey: ["reviews", "eligible", listingId, user?.id],
    queryFn: () => hasVerifiedEnquiry(user!.id, listingId),
    enabled: !!user && hasRole("customer"),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const summary = useMemo(() => summarize(reviews.filter((r) => !r.is_hidden)), [reviews]);
  const visible = useMemo(
    () =>
      sortReviews(
        reviews.filter((r) => !r.is_hidden || r.customer_id === user?.id),
        sort,
      ),
    [reviews, sort, user?.id],
  );
  const mine = reviews.find((r) => r.customer_id === user?.id) ?? null;
  const isPartner = !!user && user.id === partnerId;

  const submit = useMutation({
    mutationFn: async () => {
      if (rating < 1) throw new Error("Please select a star rating.");
      if (mine) {
        await updateOwnReview(mine.id, { rating, title, body });
      } else {
        await submitReview({
          listingId,
          listingKind,
          serviceType,
          listingTitle,
          partnerId: partnerId!,
          customerId: user!.id,
          customerName: profile?.full_name ?? null,
          rating,
          title,
          body,
        });
      }
    },
    onSuccess: () => {
      toast.success(mine ? "Your review was updated." : "Thanks! Your review is published.");
      setEditing(false);
      setTitle("");
      setBody("");
      setRating(0);
      refresh();
    },
    onError: (e: unknown) =>
      toast.error(e instanceof Error ? e.message : "Could not save your review."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteReview(id),
    onSuccess: () => {
      toast.success("Review removed.");
      refresh();
    },
    onError: () => toast.error("Could not remove the review."),
  });

  const reply = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) => replyToReview(id, text),
    onSuccess: () => {
      toast.success("Reply posted.");
      setReplyFor(null);
      setReplyText("");
      refresh();
    },
    onError: () => toast.error("Could not post the reply."),
  });

  const report = useMutation({
    mutationFn: (id: string) => reportReview(id, user!.id, "Reported from listing page"),
    onSuccess: () => toast.success("Thanks — our team will review this."),
    onError: () => toast.error("Could not report this review."),
  });

  const startEdit = () => {
    setRating(mine?.rating ?? 0);
    setTitle(mine?.title ?? "");
    setBody(mine?.body ?? "");
    setEditing(true);
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-xl bg-brand-soft text-brown-dark">
            <span className="text-lg font-bold leading-none">{summary.average || "—"}</span>
            <Star className="mt-0.5 h-3 w-3 fill-coral text-coral" aria-hidden />
          </div>
          <div>
            <StarRating value={summary.average} size="sm" />
            <p className="text-xs text-muted-foreground">
              {summary.total} {summary.total === 1 ? "review" : "reviews"}
            </p>
          </div>
        </div>

        <Select value={sort} onValueChange={(v) => setSort(v as ReviewSort)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Sort reviews">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {REVIEW_SORTS.map((s) => (
              <SelectItem key={s} value={s}>
                {REVIEW_SORT_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Write / edit form */}
      {user && hasRole("customer") && partnerId ? (
        canReview ? (
          mine && !editing ? (
            <div className="rounded-xl border border-border bg-secondary/40 p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium text-brown-dark">
                  You already reviewed this listing.
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={startEdit}>
                    <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove.mutate(mine.id)}>
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3 rounded-xl border border-border p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-brown-dark">Your rating</span>
                <StarRating value={rating} editable onChange={setRating} />
              </div>
              <Input
                placeholder="Review title (optional)"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Textarea
                placeholder="Share your experience with this listing and partner"
                value={body}
                maxLength={1000}
                rows={3}
                onChange={(e) => setBody(e.target.value)}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={() => submit.mutate()} disabled={submit.isPending}>
                  {mine ? "Update review" : "Submit review"}
                </Button>
                {editing && (
                  <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )
        ) : (
          <p className="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground">
            Only customers who have sent an enquiry for this listing can write a review.
          </p>
        )
      ) : null}

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : visible.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          No reviews yet. Be the first to rate this listing.
        </p>
      ) : (
        <ul className="space-y-3">
          {visible.map((r) => (
            <li key={r.id} className="rounded-xl border border-border p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <StarRating value={r.rating} size="sm" />
                    <span className="text-sm font-semibold text-brown-dark">
                      {r.customer_name ?? "Landoo customer"}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                    {r.is_hidden ? " · Hidden by admin" : ""}
                  </p>
                </div>
                {user && r.customer_id !== user.id && (
                  <Button size="sm" variant="ghost" onClick={() => report.mutate(r.id)}>
                    <Flag className="mr-1 h-3.5 w-3.5" /> Report
                  </Button>
                )}
              </div>
              {r.title && <p className="mt-2 text-sm font-medium text-brown-dark">{r.title}</p>}
              {r.body && (
                <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{r.body}</p>
              )}

              {r.partner_reply && (
                <div className="mt-3 rounded-lg bg-secondary/50 p-2.5">
                  <p className="text-xs font-semibold text-brown-dark">Partner reply</p>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                    {r.partner_reply}
                  </p>
                </div>
              )}

              {isPartner && (
                <div className="mt-3">
                  {replyFor === r.id ? (
                    <div className="space-y-2">
                      <Textarea
                        rows={2}
                        value={replyText}
                        maxLength={600}
                        placeholder="Write a reply to this review"
                        onChange={(e) => setReplyText(e.target.value)}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => reply.mutate({ id: r.id, text: replyText })}
                          disabled={!replyText.trim()}
                        >
                          Post reply
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setReplyFor(null)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setReplyFor(r.id);
                        setReplyText(r.partner_reply ?? "");
                      }}
                    >
                      <MessageSquareReply className="mr-1 h-3.5 w-3.5" />
                      {r.partner_reply ? "Edit reply" : "Reply"}
                    </Button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
